// src/pages/reports/components/AttemptDrawer.jsx
import React from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Input,
  Button,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { ArrowRightIcon } from '@chakra-ui/icons';

// Local helper to render tel: links safely
const Tel = ({ phone }) => {
  if (!phone || phone === '—') return <Text as="span">—</Text>;
  const href = 'tel:' + phone.replace(/[^+\d]/g, '');
  return (
    <ChakraLink href={href} color="teal.600">
      {phone}
    </ChakraLink>
  );
};

/**
 * AttemptDrawer (presentational only — no Firestore logic)
 *
 * Props:
 * - isOpen: boolean (required)
 * - onClose: () => void | Promise<void> (required)
 * - name: string (required)
 * - phone: string (required)
 * - email: string (required)
 * - attemptReason: string (required)
 * - setAttemptReason: (v: string) => void (required)
 * - attemptNotes: string (required)
 * - setAttemptNotes: (v: string) => void (required)
 * - nextCallDate: Date|null (required)
 * - setNextCallDate: (d: Date|null) => void (required)
 * - outcomePresets: Array<{ key, label, nextMs?, fixedTime? }> (required)
 * - onSaveAttempt: () => void | Promise<void> (required)
 */
export default function AttemptDrawer({
  isOpen,
  onClose,
  name,
  phone,
  email,
  attemptReason,
  setAttemptReason,
  attemptNotes,
  setAttemptNotes,
  nextCallDate,
  setNextCallDate,
  outcomePresets,
  onSaveAttempt,
}) {
  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent maxW="560px">
        <DrawerCloseButton />
        <DrawerHeader>
          <Text fontSize="lg" fontWeight="semibold">
            {name}
          </Text>
          <Text fontSize="sm" color="gray.500">
            <Tel phone={phone} /> • {email || '—'}
          </Text>
        </DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontWeight="semibold" mb={2}>
                Outcome presets
              </Text>
              <HStack wrap="wrap" spacing={2}>
                {Array.isArray(outcomePresets) &&
                  outcomePresets.map((p) => (
                    <Button
                      key={p.key}
                      size="xs"
                      onClick={() => {
                        setAttemptReason(p.key);
                        const now = new Date();
                        const when = p.fixedTime ? p.fixedTime(now) : new Date(now.getTime() + (p.nextMs || 0));
                        setNextCallDate(when);
                      }}
                    >
                      {p.label}
                    </Button>
                  ))}
              </HStack>
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={1}>
                Reason code
              </Text>
              <Input
                placeholder="e.g., NO_ANSWER, BUSY, VM_LEFT…"
                value={attemptReason}
                onChange={(e) => setAttemptReason(e.target.value)}
                size="sm"
              />
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={1}>
                Notes
              </Text>
              <Input
                placeholder="Notes…"
                value={attemptNotes}
                onChange={(e) => setAttemptNotes(e.target.value)}
                size="sm"
              />
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={1}>
                Next call (optional)
              </Text>
              <Input
                type="datetime-local"
                value={
                  nextCallDate
                    ? new Date(nextCallDate.getTime() - nextCallDate.getTimezoneOffset() * 60000)
                        .toISOString()
                        .slice(0, 16)
                    : ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  setNextCallDate(v ? new Date(v) : null);
                }}
                size="sm"
              />
            </Box>
          </VStack>
        </DrawerBody>
        <DrawerFooter>
          <HStack>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="teal" leftIcon={<ArrowRightIcon />} onClick={onSaveAttempt}>
              Save attempt
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
